import { FundStage, resolvePreIpoFundStage } from '@domain/funds/model'
import { formatNumberWithShortcuts } from '@utils/format.utils'
import type {
  PreIpoPrototypePage,
  PrototypeAllocation,
  PrototypeBuyStep,
  PrototypeFaq,
  PrototypeGridItem,
  PrototypeHeroStat,
  PrototypeHighlightStat,
  PrototypeHowItWorksStep,
} from './prototypePage.types'

export const SHARED_HOW_IT_WORKS_INTRO = 'Pre-IPO access in four steps.'

export const SHARED_HOW_IT_WORKS_STEPS: PrototypeHowItWorksStep[] = [
  { step: '1', label: 'Sign up', detail: 'Create your WLTH account' },
  { step: '2', label: 'Fund wallet', detail: 'Add funds to invest' },
  { step: '3', label: 'Invest', detail: 'Choose your amount' },
  { step: '4', label: 'Get Slice', detail: 'Equity-backed token in your wallet' },
]

export const SHARED_BUYING_INTRO =
  "Pre-IPO means investing before a company lists publicly, when access is usually locked to insiders. Here's how WLTH opens it up."

export const SHARED_BUYING_STEPS: PrototypeBuyStep[] = [
  {
    label: 'A private round',
    detail: 'A company raised capital at a pre-IPO valuation, long before any IPO.',
  },
  {
    label: 'WLTH gets an allocation',
    detail:
      'WLTH secures a slice of that round, normally reserved for large institutions.',
  },
  {
    label: 'You own a Slice',
    detail:
      'You get tokenized economic exposure to the underlying asset backed 1:1 and verifiable. Self custodied and tradable at any time.',
  },
]

export function buildBuyingSteps(
  companyName: string,
  companyValuation: string,
): PrototypeBuyStep[] {
  return [
    {
      label: 'A private round',
      detail: `${companyName} raised capital at a ~${companyValuation} company valuation, long before any IPO.`,
    },
    {
      label: 'WLTH gets an allocation',
      detail:
        'WLTH secures a slice of that round, normally reserved for large institutions.',
    },
    {
      label: 'You own a Slice',
      detail:
        'You get tokenized economic exposure to the underlying asset backed 1:1 and verifiable. Self custodied and tradable at any time.',
    },
  ]
}

export const SHARED_BUYING_FOOTNOTE =
  'No paperwork, no five-figure minimums. Own a Slice from $20, with payouts in USDC.'

export const SHARED_PRODUCT_FAQS: PrototypeFaq[] = [
  {
    q: 'What is a WLTH Slice?',
    a: 'A tokenized position backed 1:1 by real underlying equity in the company, not a synthetic price tracker.',
  },
  {
    q: "What's the minimum?",
    a: 'You can invest from $20. Available to eligible users outside the US and UK.',
  },
  {
    q: 'Can I sell before an IPO?',
    a: 'You may be able to list your Slice on the WLTH marketplace, subject to terms and demand. Liquidity is not guaranteed.',
  },
  {
    q: 'How is my Slice backed?',
    a: 'Your Slice is a contractual economic right tied to a WLTH allocation. Review the fund offering documents for structure and risks.',
  },
]

export const SHARED_RISK_FOOTNOTE =
  'Private-market investment. Capital is at risk and values can fall as well as rise. This is not investment advice.'

export const EQUITY_BACKED_CALLOUT_BEFORE = 'Each Slice is backed 1:1 by '
export const EQUITY_BACKED_CALLOUT_VERIFIED_LABEL = 'verified'
export const EQUITY_BACKED_CALLOUT_AFTER = ' underlying equity in this company.'
export const EQUITY_BACKED_AUDIT_TITLE = 'Equity audit'
export const EQUITY_BACKED_AUDIT_OPEN_LABEL = 'Download audit'
export const EQUITY_BACKED_AUDIT_EMPTY =
  'The audit for this company has not been published yet.'

export const WLTH_ENTRY_LABEL = 'WLTH entry'
export const IN_APP_TRADING_LABEL = 'In-app'
/** @deprecated Use IN_APP_TRADING_LABEL */
export const MARKETPLACE_ENTRY_LABEL = IN_APP_TRADING_LABEL
export const COMPANY_VALUATION_LABEL = 'Company valuation'

export function isCompanyValuationLabel(label: string) {
  const normalized = label.toLowerCase()
  return (
    normalized === COMPANY_VALUATION_LABEL.toLowerCase() ||
    normalized === 'current valuation'
  )
}

export function isInAppTradingLabel(label: string) {
  const normalized = label.toLowerCase()
  return (
    normalized === IN_APP_TRADING_LABEL.toLowerCase() ||
    normalized === 'in-app/trading' ||
    normalized === 'marketplace entry' ||
    normalized === 'in-app valuation'
  )
}

export function isClosedDealStage(stage?: string | null) {
  return resolvePreIpoFundStage(stage) === FundStage.Closed
}

export function isPausedDealStage(stage?: string | null) {
  return resolvePreIpoFundStage(stage) === FundStage.Paused
}

/** Allocation / raising UI — hidden only when the deal is Closed. */
export function showDealAllocation(stage?: string | null) {
  return !isClosedDealStage(stage)
}

export const VALUATIONS_EXPLAINED_TITLE = 'Valuations explained'

export const VALUATION_EXPLAINER_COPY = {
  wlthEntry: {
    label: 'WLTH entry',
    short: 'Valuation of the round WLTH bought into. New Slices are priced from this.',
    description:
      'The valuation of the private round where WLTH secured this allocation. New Slices issued from this raise are priced from this entry.',
  },
  marketplaceEntry: {
    label: IN_APP_TRADING_LABEL,
    short: 'Lowest implied valuation from live marketplace listings.',
    description:
      'The lowest implied valuation from live Slice listings on the WLTH marketplace. It moves with what holders are asking, not with a new company round.',
  },
  companyValuation: {
    label: 'Company valuation',
    short: 'Latest reported company valuation.',
    description:
      'The company’s latest reported valuation (last private round, or live market cap after IPO). This is the headline number, not what you pay on WLTH.',
  },
} as const

export function buildDemoAllocation(seed: number): PrototypeAllocation {
  const totalValue = 250000
  const percentFilled = 72 + (seed % 18)
  const raisedValue = Math.round((totalValue * percentFilled) / 100)
  const remainingValue = totalValue - raisedValue
  return {
    percentFilled,
    raised: `$${raisedValue.toLocaleString('en-US')} raised`,
    remaining: `$${remainingValue.toLocaleString('en-US')} of $250K left`,
    raisedValue,
    totalValue,
  }
}

export function buildAllocationFromRemaining(
  remainingValue: number,
  totalValue = 250000,
): PrototypeAllocation {
  const remaining = Math.max(0, Math.min(totalValue, remainingValue))
  const raisedValue = totalValue - remaining
  const percentFilled = Math.round((raisedValue / totalValue) * 100)
  return {
    percentFilled,
    raised: `$${raisedValue.toLocaleString('en-US')} raised`,
    remaining: `$${remaining.toLocaleString('en-US')} of $250K left`,
    raisedValue,
    totalValue,
  }
}

export function formatUsdShortcut(value: number): string {
  if (!value || !Number.isFinite(value) || value <= 0) return ''
  return `$${formatNumberWithShortcuts(value)}`
}

function isUsableValuation(value?: string) {
  if (!value) return false
  const trimmed = value.trim()
  return trimmed.length > 0 && trimmed !== '—' && trimmed !== '-'
}

/** Company valuation for slice cards / buying copy. Skips empty and em-dash placeholders. */
export function pickSliceValuation(
  highlightStats: PrototypeHighlightStat[],
  fallbacks: { currentValuation?: number; entryValuation?: number } = {},
): string {
  const byLabel = (label: string) =>
    highlightStats.find((stat) => stat.label === label)?.value

  return (
    [
      byLabel(COMPANY_VALUATION_LABEL),
      byLabel('Current Valuation'),
      byLabel(WLTH_ENTRY_LABEL),
      byLabel('WLTH Entry'),
      byLabel(IN_APP_TRADING_LABEL),
      byLabel(MARKETPLACE_ENTRY_LABEL),
      byLabel('In-app/trading'),
      byLabel('Marketplace entry'),
      byLabel('In-App Valuation'),
      formatUsdShortcut(fallbacks.currentValuation ?? 0),
      formatUsdShortcut(fallbacks.entryValuation ?? 0),
    ].find(isUsableValuation) ?? ''
  )
}

export function buildInvestBarSubtitle(
  entryValuation: string,
  minInvestment = 20,
): string {
  return `From $${minInvestment} · ${entryValuation}`
}

export function buildValuationDisplay({
  minInvestment,
  entryValuation,
  currentValuation,
  inAppValuation,
}: {
  stage?: string
  minInvestment: number
  /** WLTH entry / reference valuation. */
  entryValuation: number
  /** Latest company valuation. */
  currentValuation: number
  /** Marketplace-implied valuation. */
  inAppValuation?: number
}): {
  highlightStats: PrototypeHighlightStat[]
  heroStats: PrototypeHeroStat[]
  investBarSubtitle: string
} {
  const entry = formatUsdShortcut(entryValuation)
  const current = formatUsdShortcut(currentValuation)
  const inApp = formatUsdShortcut(inAppValuation ?? 0)
  const from = `$${minInvestment}`
  const lowestForBar = [entryValuation, inAppValuation ?? 0]
    .filter((value) => Number.isFinite(value) && value > 0)
    .reduce<
      number | null
    >((lowest, value) => (lowest === null ? value : Math.min(lowest, value)), null)
  const lowest = formatUsdShortcut(lowestForBar ?? 0)

  const highlightStats = [
    ...(entry ? [{ label: WLTH_ENTRY_LABEL, value: entry }] : []),
    ...(inApp ? [{ label: IN_APP_TRADING_LABEL, value: inApp }] : []),
    ...(current ? [{ label: COMPANY_VALUATION_LABEL, value: current }] : []),
  ]

  return {
    highlightStats,
    heroStats: highlightStats,
    investBarSubtitle: lowest
      ? buildInvestBarSubtitle(lowest, minInvestment)
      : current || `From ${from}`,
  }
}

export const SHARED_PLATFORM_USPS: PrototypeGridItem[] = [
  { label: 'Start from', value: '$20' },
  { label: 'Structure', value: 'Slice' },
  { label: 'Liquidity', value: 'Marketplace' },
]

const DEAL_TERMS_META_ORDER = ['Start from', 'Structure', 'Liquidity'] as const

/** Structure / Liquidity / Start from only. Valuations render in their own block. */
export function buildOverviewDealTerms(
  investmentGrid: PrototypeGridItem[],
  _highlightStats?: PrototypeHighlightStat[],
): PrototypeGridItem[] {
  const byLabel = new Map<string, PrototypeGridItem>()
  for (const item of investmentGrid) {
    byLabel.set(item.label, item)
  }

  return DEAL_TERMS_META_ORDER.flatMap((label) => {
    const item = byLabel.get(label)
    return item ? [item] : []
  })
}

export function buildSharedInvestmentGrid(
  stage: string,
): PreIpoPrototypePage['investmentGrid'] {
  return [
    SHARED_PLATFORM_USPS[0],
    SHARED_PLATFORM_USPS[1],
    { label: 'Stage', value: stage },
    SHARED_PLATFORM_USPS[2],
  ]
}

export function buildRiskSection(companyName: string): string {
  return (
    '<h4>Key risks</h4>' +
    '<ul>' +
    '<li><strong>Illiquidity.</strong> Pre-IPO holdings cannot be readily sold; any marketplace exit is subject to demand and terms and is not guaranteed.</li>' +
    '<li><strong>Stage &amp; valuation.</strong> Private-market valuations may not be realized and capital is at risk.</li>' +
    '<li><strong>Competition.</strong> Well-funded rivals could affect market share and future financing.</li>' +
    '<li><strong>Regulation.</strong> Evolving rules in AI, fintech, or defense could affect ' +
    companyName +
    '&rsquo;s operations or valuation.</li>' +
    '</ul>' +
    '<h4>Liquidity &amp; exit scenarios</h4>' +
    '<p>Potential paths include a future IPO, a secondary sale, or listing your Slice on the WLTH marketplace. Timing and availability are uncertain. This summary is information, not investment advice.</p>'
  )
}
